import { useEffect, useMemo, useRef, useState } from "react";
import type {
  WidgetBrand,
  WidgetCombo,
  WidgetService,
  WidgetTenant,
} from "../types";
import { BookingSetup } from "./BookingSetup";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../lib/image";
import { showWhatsMissing } from "../lib/show-whats-missing";
import { useCart } from "../state/cart";
import {
  getCartToken,
  type BookingWindow,
} from "../state/booking";
import {
  checkComboAvailability,
  comboConditionsLabel,
  comboPricingLabel,
  createComboHold,
  estimateComboPrice,
  type ComboPriceEstimate,
  type ComboSelection,
} from "../state/combos";
import {
  loadServiceHours,
  type ServiceHoursRow,
} from "../state/service-hours";

// The virtual __packages__ tab — curated multi-service bundles (Stay + Spa)
// from the combos schema. List → per-component configurator → live bundle
// price via resolve_combo_price → create_combo_hold puts one pending hold
// per component under the shared cart token, so the normal cart/checkout
// flow takes over unchanged.
export function CombosPanel({
  combos,
  services,
  tenant,
  activeBrand,
  label = "Packages",
  onAddedToCart,
  initialSelectedId,
  onConsumeInitialSelection,
}: {
  combos: WidgetCombo[];
  /** The widget's visible services — components resolve against these. */
  services: WidgetService[];
  tenant: WidgetTenant;
  activeBrand: WidgetBrand | null;
  /** Tenant-set title (settings.packages_tab_label) — the browsing-screen
   *  heading + back chip; defaults to the historical "Packages". */
  label?: string;
  onAddedToCart: () => void;
  /** Open straight into this combo instead of the browsing list. Set when a
   *  guest clicks a named offer — landing them on the list means none of that
   *  offer's own defaults (party size, permitted days, nights) ever apply. */
  initialSelectedId?: string | null;
  onConsumeInitialSelection?: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSelectedId ?? null,
  );

  // Apply the target once, then tell the parent to clear it, so going Back to
  // the list (or re-mounting the tab) doesn't snap straight back into it.
  // Same shape as EventsPanel's deep-link effect.
  useEffect(() => {
    if (initialSelectedId) {
      setSelectedId(initialSelectedId);
      onConsumeInitialSelection?.();
    }
    // Deliberately not depending on onConsumeInitialSelection — it changes
    // identity every render in the parent, which would refire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedId]);
  const selected = selectedId
    ? combos.find((c) => c.id === selectedId) ?? null
    : null;

  if (selected) {
    return (
      <ComboDetail
        combo={selected}
        services={services}
        tenant={tenant}
        activeBrand={activeBrand}
        label={label}
        onBack={() => setSelectedId(null)}
        onAdded={onAddedToCart}
      />
    );
  }

  if (combos.length === 0) {
    return (
      <div className="vt-events-empty">
        <strong>No packages right now.</strong>
        <p>Check back soon — new packages get posted regularly.</p>
      </div>
    );
  }

  return (
    <div className="vt-events-panel">
      <h3 className="vt-events-group-head">{label}</h3>
      <ul className="vt-events-list">
        {combos.map((combo) => (
          <ComboCard
            key={combo.id}
            combo={combo}
            services={services}
            onSelect={() => setSelectedId(combo.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function ComboCard({
  combo,
  services,
  onSelect,
}: {
  combo: WidgetCombo;
  services: WidgetService[];
  onSelect: () => void;
}) {
  const componentNames = combo.combo_components
    .map(
      (c) => services.find((s) => s.id === c.service_id)?.name ?? null,
    )
    .filter((n): n is string => n !== null);
  const conditions = comboConditionsLabel(combo);
  const dateLine = [comboPricingLabel(combo), conditions]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="vt-event-card">
      <div className="vt-event-date-line">{dateLine}</div>
      <h3 className="vt-event-name">{combo.name}</h3>
      {combo.description && (
        <p className="vt-event-summary">{combo.description}</p>
      )}
      {componentNames.length > 0 && (
        <div className="vt-event-tiers">
          {componentNames.map((n, i) => (
            <div key={i} className="vt-event-tier">
              <span className="vt-event-tier-name">{n}</span>
              <span className="vt-event-tier-price">included</span>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="vt-event-card-button" onClick={onSelect}>
        Build this package →
      </button>
    </li>
  );
}

// ─── Detail / configurator ────────────────────────────────────────────────

function ComboDetail({
  combo,
  services,
  tenant,
  activeBrand,
  label,
  onBack,
  onAdded,
}: {
  combo: WidgetCombo;
  services: WidgetService[];
  tenant: WidgetTenant;
  activeBrand: WidgetBrand | null;
  /** The browsing screen's title — names the back chip's destination. */
  label: string;
  onBack: () => void;
  onAdded: () => void;
}) {
  const { add } = useCart();

  // Resolve each component against the widget's visible services. A
  // component whose service isn't surfaced by this widget can't be
  // configured — flagged below and the CTA stays disabled.
  const parts = useMemo(
    () =>
      combo.combo_components.map((component) => ({
        component,
        service: services.find((s) => s.id === component.service_id) ?? null,
      })),
    [combo, services],
  );
  const missing = parts.filter((p) => p.service === null);
  // Events-type components need EventsSetup's event-unit picker, which this
  // configurator doesn't host yet — block instead of rendering a dead form.
  const unsupported = parts.filter(
    (p) => p.service?.service_type === "events",
  );
  const blocked = missing.length > 0 || unsupported.length > 0;
  // Unit-less components (e.g. dining) are deposit-priced at hold time; the
  // pre-hold estimate can't include that, so disclose it.
  const hasUnitless = parts.some(
    (p) =>
      p.service !== null &&
      p.component.service_unit_id === null &&
      p.service.units.length === 0,
  );

  const [windows, setWindows] = useState<Record<string, BookingWindow | null>>(
    {},
  );
  const [unitSel, setUnitSel] = useState<Record<string, string | null>>(() => {
    const init: Record<string, string | null> = {};
    for (const { component, service } of parts) {
      // Never pre-select a room the server would refuse (accommodation with
      // no rate loaded — the unpriced-hold guard rejects it at book time).
      const firstBookable =
        service?.service_type === "accommodation"
          ? service.units.find((u) => u.base_price != null)
          : service?.units[0];
      init[component.service_id] =
        component.service_unit_id ?? firstBookable?.id ?? null;
    }
    return init;
  });
  const [hoursByService, setHoursByService] = useState<
    Record<string, ServiceHoursRow[]>
  >({});
  const [estimate, setEstimate] = useState<ComboPriceEstimate | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Operating hours per component service (BookingSetup uses them to
  // constrain its pickers). Fail-open like everywhere else.
  useEffect(() => {
    let cancelled = false;
    for (const { service } of parts) {
      if (!service) continue;
      loadServiceHours(service.id).then((rows) => {
        if (cancelled) return;
        setHoursByService((prev) => ({ ...prev, [service.id]: rows }));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [parts]);

  const selections: ComboSelection[] | null = useMemo(() => {
    const out: ComboSelection[] = [];
    for (const { component, service } of parts) {
      if (!service) return null;
      const win = windows[component.service_id];
      if (!win) return null;
      const unitId = unitSel[component.service_id] ?? null;
      const unit = unitId
        ? service.units.find((u) => u.id === unitId)
        : undefined;
      out.push({
        service_id: component.service_id,
        service_unit_id: unitId,
        // Deposit-priced leg: the estimate prices it like the hold will.
        unit_unpriced: unit != null && unit.base_price == null,
        starts_at: win.starts_at,
        ends_at: win.ends_at,
        party_size: win.party_size,
      });
    }
    return out;
  }, [parts, windows, unitSel]);

  // Live bundle estimate whenever the configuration completes/changes.
  // Debounced (each estimate is an RPC volley — one resolve_unit_price per
  // unit component + resolve_combo_price) and latest-wins guarded so a
  // stale slow response can't clobber a newer one.
  useEffect(() => {
    if (!selections) {
      setEstimate(null);
      return;
    }
    let stale = false;
    const t = setTimeout(() => {
      estimateComboPrice(combo, selections).then((res) => {
        if (!stale) setEstimate(res);
      });
    }, 400);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [combo, selections]);

  // A configuration change invalidates the "Package held" success state —
  // re-enable the CTA for the new configuration.
  useEffect(() => {
    setFeedback(null);
  }, [windows, unitSel]);

  async function handleBook() {
    if (!selections || pending) return;
    setPending(true);
    setError(null);
    try {
      const avail = await checkComboAvailability(combo.id, selections);
      if (!avail.ok) {
        setError(describeComboReason(avail.reason));
        return;
      }
      const hold = await createComboHold({
        comboId: combo.id,
        selections,
        cartToken: getCartToken(),
      });
      if (!hold.ok) {
        setError(describeComboReason(hold.reason, (hold as { detail?: unknown }).detail));
        return;
      }
      // One cart line per component hold (allocations carry the discounted
      // split; booking_ids is in selection order and allocation.ref is the
      // component booking id).
      hold.booking_ids.forEach((bookingId, i) => {
        const sel = selections[i];
        if (!sel) return;
        const part = parts.find((p) => p.component.service_id === sel.service_id);
        const service = part?.service;
        if (!service) return;
        const unit =
          service.units.find((u) => u.id === sel.service_unit_id) ?? null;
        const alloc = hold.allocations.find((a) => a.ref === bookingId);
        add({
          serviceId: service.id,
          serviceName: service.name,
          serviceType: service.service_type,
          unitId: unit?.id ?? null,
          unitName: unit?.name ?? null,
          description: `${combo.name} · ${unit?.name ?? service.name}`,
          quantity: 1,
          unitPrice: alloc ? alloc.amount_cents / 100 : null,
          // Tenant currency, NOT combo.currency: the Paystack charge and the
          // cart footer both resolve the tenant's currency, so a stray combo
          // currency value must never relabel the line.
          currency: tenant.currency ?? "ZAR",
          brandId: service.brand_id,
          brandName: activeBrand?.name ?? null,
          metadata: {
            booking_id: bookingId,
            starts_at: sel.starts_at,
            ends_at: sel.ends_at,
            party_size: sel.party_size,
            source: "combo",
            combo_id: combo.id,
            combo_name: combo.name,
          },
        });
      });
      setFeedback("Package held — added to cart");
      onAdded();
    } finally {
      setPending(false);
    }
  }

  const conditions = comboConditionsLabel(combo);

  // The estimate is only an advisory PREVIEW (state/combos.ts) — the
  // authoritative gate is checkComboAvailability + createComboHold on Book.
  // Only DETERMINISTIC reasons (wrong day/party/nights/off-sale, or the combo
  // vanished) should block booking and raise the red banner. A transient
  // estimate RPC blip surfaces as reason "network_error"/an error string; we
  // must NOT hard-block a valid combo on that — let Book run and let the
  // server decide (it re-validates and surfaces any real error itself).
  const hardBlocked =
    estimate != null &&
    !estimate.ok &&
    (estimate.reason === "conditions_not_met" ||
      estimate.reason === "combo_not_found" ||
      estimate.reason === "combo_unavailable");

  // Rate-less accommodation the guest can do nothing about: the component's
  // PINNED room is unpriced, or every selectable room is. The server rejects
  // such holds (unpriced_accommodation_unit) — and its "pick another room"
  // copy is a dead end when there's no picker — so say it up front and keep
  // Book disabled. Also stops the estimate pill advertising a total that
  // silently omits the room (resolve_unit_price prices null as 0).
  const unpricedBlocked = parts.some(({ component, service }) => {
    if (!service || service.service_type !== "accommodation") return false;
    if (component.service_unit_id) {
      const pinned = service.units.find(
        (u) => u.id === component.service_unit_id,
      );
      return pinned != null && pinned.base_price == null;
    }
    return (
      service.units.length > 0 &&
      service.units.every((u) => u.base_price == null)
    );
  });

  // Book can't go yet: the button says why, and where the guest can fix it a
  // press shows where (lib/show-whats-missing.ts): the first part without
  // dates, or the reason the package doesn't apply. A part that is gone or a
  // room without rates can't be fixed here: it says so and stays off.
  const rootRef = useRef<HTMLDivElement | null>(null);
  function showPartWithoutDates() {
    const part = parts.find(
      ({ component, service }) =>
        service && service.service_type !== "events" && !windows[component.service_id],
    );
    const root = rootRef.current;
    if (!part || !root) return;
    showWhatsMissing(
      root.querySelector<HTMLElement>(`[data-combo-part="${part.component.service_id}"]`),
    );
  }
  function showComboReason() {
    // The reason banner; while a failed Book's error shows instead, that one.
    const root = rootRef.current;
    showWhatsMissing(
      root?.querySelector<HTMLElement>(".vt-combo-reason") ??
        root?.querySelector<HTMLElement>(".vt-capacity-error"),
    );
  }
  const bookFix: { label: string; fix: (() => void) | null } | null =
    blocked || unpricedBlocked
      ? { label: "Package unavailable", fix: null }
      : !selections
        ? { label: "Pick dates for each part", fix: showPartWithoutDates }
        : hardBlocked
          ? { label: "Change your choices ↑", fix: showComboReason }
          : null;

  return (
    <div className="vt-events-panel" ref={rootRef}>
      <button type="button" className="vt-chip" onClick={onBack}>
        ‹ All {label}
      </button>

      <div className="vt-event-date-line" style={{ marginTop: 12 }}>
        {comboPricingLabel(combo)}
        {conditions ? ` · ${conditions}` : ""}
      </div>
      <h3 className="vt-event-name">{combo.name}</h3>
      {combo.description && (
        <p className="vt-event-summary">{combo.description}</p>
      )}

      {blocked && (
        <div className="vt-capacity-error">
          Part of this package isn&apos;t bookable online right now — please
          contact the venue.
        </div>
      )}

      {parts.map(({ component, service }) => {
        if (!service || service.service_type === "events") return null;
        const openUnitChoice =
          component.service_unit_id === null && service.units.length > 0;
        const chosenUnitId = unitSel[component.service_id] ?? null;
        return (
          <section
            key={component.id}
            data-combo-part={component.service_id}
            style={{ marginTop: 16, display: "grid", gap: 8 }}
          >
            <div className="vt-divider">{service.name}</div>
            {openUnitChoice &&
              (service.service_type === "accommodation" ? (
                // Accommodation shows ROOM CARDS (photo + name + per-night
                // price), not chips — a room is a considered choice.
                <div className="vt-combo-rooms">
                  {service.units.map((u) => {
                    const on = u.id === chosenUnitId;
                    // Mirrors the Stay tab: a rate-less room reads "Rates
                    // unavailable" and can't be picked — the server-side
                    // unpriced-hold guard would reject it anyway, this just
                    // says why before the guest builds a package around it.
                    const unpriced = u.base_price == null;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        aria-pressed={on}
                        disabled={unpriced}
                        className={`vt-combo-room${on ? " vt-combo-room-active" : ""}${unpriced ? " vt-combo-room-unpriced" : ""}`}
                        onClick={() =>
                          setUnitSel((prev) => ({
                            ...prev,
                            [component.service_id]: u.id,
                          }))
                        }
                      >
                        {u.image_url && (
                          <img
                            className="vt-combo-room-img"
                            style={u.image_position ? { objectPosition: u.image_position } : undefined}
                            src={optimizedImageUrl(u.image_url, { width: 400 })}
                            srcSet={optimizedSrcSet(u.image_url, IMG_WIDTHS.CARD)}
                            sizes="(min-width: 600px) 33vw, 100vw"
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <span className="vt-combo-room-body">
                          <span className="vt-combo-room-name">{u.name}</span>
                          {unpriced ? (
                            <span className="vt-combo-room-price vt-combo-room-price-unavail">
                              Rates unavailable
                            </span>
                          ) : (
                            service.show_prices_in_widget && (
                              <span className="vt-combo-room-price">
                                R {Number(u.base_price).toLocaleString()}
                                <span className="vt-combo-room-per"> / night</span>
                              </span>
                            )
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {service.units.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className={`vt-chip ${u.id === chosenUnitId ? "vt-chip-active" : ""}`}
                      onClick={() =>
                        setUnitSel((prev) => ({
                          ...prev,
                          [component.service_id]: u.id,
                        }))
                      }
                    >
                      {u.name}
                      {service.show_prices_in_widget && u.base_price != null
                        ? ` · R ${Number(u.base_price).toLocaleString()}`
                        : ""}
                    </button>
                  ))}
                </div>
              ))}
            <BookingSetup
              service={service}
              window={windows[component.service_id] ?? null}
              selectedEventUnit={null}
              onChange={(w) =>
                setWindows((prev) => ({ ...prev, [component.service_id]: w }))
              }
              busyIntervals={[]}
              serviceHours={hoursByService[service.id] ?? []}
              // A package with fixed nights (buy-X-pay-Y "stay for N", or a
              // min-nights bundle) auto-sets the stay length for accommodation.
              // Only fixed_price is capped at the component's pinned quantity
              // — it does not scale with the window — so only that kind LOCKS
              // the picker.
              //
              // buy_qty and min_nights used to fall through to this lock, which
              // contradicted the comment right above them and cost real money:
              // "Stay & Save 25%, minimum 2 nights" pinned the stay at exactly
              // two, so a guest wanting three could not book it. A minimum is a
              // floor, not a length. It is passed as one below.
              fixedNights={
                service.service_type === "accommodation" &&
                combo.pricing_kind === "fixed_price"
                  ? (combo.combo_components?.find(
                      (c) => c.service_id === service.id,
                    )?.quantity ?? null)
                  : null
              }
              minNights={
                service.service_type === "accommodation"
                  ? (combo.buy_qty ?? combo.min_nights ?? null)
                  : null
              }
              // Pre-set the package's own conditions as defaults so it opens
              // already eligible: required headcount + a valid booking day.
              // Prefer the required floor (min); else, if only a cap is set,
              // open at the cap (or 2, whichever is smaller) so a "max 1"
              // package doesn't open above its own limit.
              defaultPartySize={
                combo.min_party_size ??
                (combo.max_party_size != null
                  ? Math.min(2, combo.max_party_size)
                  : null)
              }
              allowedDaysOfWeek={combo.days_of_week}
            />
          </section>
        );
      })}

      {error && <div className="vt-capacity-error">{error}</div>}
      {/* Why the package can't be applied for the current selection (wrong day,
          party size, nights, or the package is gone). Promoted from a subtle
          inline hint to the same red error banner used elsewhere so it can't be
          missed. Only shown for deterministic reasons — a transient estimate
          blip must not paint an alarming error over a bookable package. */}
      {!error && hardBlocked && (
        <div className="vt-capacity-error vt-combo-reason">
          {describeComboReason(estimate!.reason, estimate!.detail)}
        </div>
      )}
      {!error && !hardBlocked && unpricedBlocked && (
        <div className="vt-capacity-error">
          The room in this package doesn&apos;t have rates loaded yet, so it
          can&apos;t be booked online — please send an enquiry or check back
          soon.
        </div>
      )}

      <div className="vt-action-row" style={{ marginTop: 16 }}>
        {estimate?.ok && !unpricedBlocked ? (
          <div className="vt-status-pill" role="status">
            <span className="vt-status-pill-dot" aria-hidden="true" />
            <span className="vt-status-pill-chips">
              {estimate.discount_cents > 0
                ? `R ${(estimate.base_total_cents / 100).toLocaleString()} → save R ${(estimate.discount_cents / 100).toLocaleString()}`
                : "Package total"}
            </span>
            <span className="vt-status-pill-total">
              = R {(estimate.bundle_total_cents / 100).toLocaleString()}
            </span>
          </div>
        ) : (
          // Why Book can't go is on the button itself (bookFix), and the red
          // banner above gives the full reason.
          <div className="vt-action-status" />
        )}
        <button
          type="button"
          className={`vt-cta-inline${
            feedback ? " vt-cta-inline-success" : bookFix?.fix ? " vt-cta-blocked" : ""
          }`}
          onClick={bookFix?.fix ?? handleBook}
          disabled={
            pending ||
            feedback != null ||
            (bookFix !== null && bookFix.fix === null)
          }
          aria-disabled={bookFix?.fix ? true : undefined}
        >
          {feedback ?? (pending ? "Holding…" : bookFix?.label ?? "Book package →")}
        </button>
      </div>
      {hasUnitless && estimate?.ok && (
        <div className="vt-general-hint">
          The total includes venue deposits for parts priced by deposit — the
          cart shows the final split.
        </div>
      )}
    </div>
  );
}

function describeComboReason(reason: string, detail?: unknown): string {
  switch (reason) {
    case "selection_not_in_combo":
    case "combo_components_mismatch":
      return "That selection doesn't match this package — please rebuild it from the package options.";
    case "combo_window_unbounded":
      return "This package needs its stay length configured before it can be booked — please contact us.";
    case "component_unit_required":
      return "Please choose a room for the stay in this package.";
    case "combo_not_priced":
      return "We couldn't price this package for those dates. Please try different dates or contact us.";
    case "conditions_not_met":
      switch (detail) {
        case "day_of_week":
          return "This package isn't available on that day.";
        case "min_party_size":
        case "max_party_size":
          return "This package isn't available for that party size.";
        case "min_nights":
          return "This package needs a longer stay.";
        case "sales_not_open":
        case "sales_closed":
          return "This package isn't on sale right now.";
        default:
          return "This package isn't available for that selection.";
      }
    case "component_unavailable":
      return "One part of the package isn't available at those times — try different dates.";
    case "component_capacity":
      return "One part of the package is fully booked at those times.";
    case "component_hold_failed":
      return "Couldn't hold every part of the package — try different times.";
    case "combo_not_found":
    case "combo_unavailable":
      return "This package is no longer available.";
    case "unpriced_accommodation_unit":
      return "That room doesn't have rates loaded yet, so it can't be booked online — pick another room or send an enquiry.";
    default:
      return `Couldn't book the package (${reason}).`;
  }
}
